/**
 * Premium CTA Tags
 *
 * Parses premium upgrade markers emitted by chat and tool streams.
 *
 * Primary responsibilities:
 * - Find bracket and custom-tag premium CTA markers
 * - Split markdown around CTAs before markdown rendering
 *
 * Dependencies:
 * - none
 */

export type PremiumCtaMarker = {
  end: number
  payloadText: string
  rawText: string
  start: number
}

export type PremiumCtaSegment =
  | {
      text: string
      type: 'text'
    }
  | {
      payloadText: string
      type: 'cta'
    }

const BRACKET_CTA_REGEX =
  /\\?\[GET_PREMIUM\\?\]([\s\S]*?)\\?\[\/GET_PREMIUM\\?\]/g
const STANDALONE_CTA_REGEX = /\\?\[GET_PREMIUM\\?\]/g
const CUSTOM_CTA_REGEX =
  /(?:<|&lt;)get-premium-button\b([\s\S]*?)(?:\/\s*(?:>|&gt;)|(?:>|&gt;)[\s\S]*?(?:&lt;\/get-premium-button\s*&gt;|<\/get-premium-button\s*>))/gi
const ORPHAN_CTA_REGEX =
  /\bdata-payload\s*=\s*(?:\\"([^\\"]*)\\"|"([^"]*)"|'([^']*)'|&quot;([\s\S]*?)&quot;|&#34;([\s\S]*?)&#34;|([^\s>]+))\s*(?:>|&gt;)?\s*(?:&lt;\/get-premium-button\s*&gt;|<\/get-premium-button\s*>)/gi
const PAYLOAD_ATTRIBUTE_REGEX =
  /\bdata-payload\s*=\s*(?:\\"([^\\"]*)\\"|"([^"]*)"|'([^']*)'|&quot;([\s\S]*?)&quot;|&#34;([\s\S]*?)&#34;|([^\s>]+))/i

const decodeMarkerEntities = (value: string) =>
  value
    .replace(/&quot;|&#34;/g, '"')
    .replace(/&apos;|&#39;/g, "'")
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&amp;/g, '&')

const decodePayloadText = (value: string | null | undefined) => {
  if (!value) return ''

  const decodedEntities = decodeMarkerEntities(value.trim())

  try {
    return decodeURIComponent(decodedEntities)
  } catch {
    return decodedEntities
  }
}

const getCustomTagPayload = (attributes: string | null | undefined) => {
  const payloadMatch = String(attributes || '').match(PAYLOAD_ATTRIBUTE_REGEX)

  return decodePayloadText(
    payloadMatch?.[1] ||
      payloadMatch?.[2] ||
      payloadMatch?.[3] ||
      payloadMatch?.[4] ||
      payloadMatch?.[5] ||
      payloadMatch?.[6]
  )
}

const pushMarker = (
  markers: PremiumCtaMarker[],
  start: number,
  rawText: string,
  payloadText: string
) => {
  if (!rawText) return

  markers.push({
    end: start + rawText.length,
    payloadText,
    rawText,
    start
  })
}

const byStartThenLength = (a: PremiumCtaMarker, b: PremiumCtaMarker) =>
  a.start - b.start || b.rawText.length - a.rawText.length

const removeOverlappingMarkers = (markers: PremiumCtaMarker[]) => {
  const result: PremiumCtaMarker[] = []
  let coveredUntil = -1

  for (const marker of [...markers].sort(byStartThenLength)) {
    if (marker.start < coveredUntil) continue

    result.push(marker)
    coveredUntil = marker.end
  }

  return result
}

export const findPremiumCtaMarkers = (content: string | null | undefined) => {
  if (!content) return []

  const markers: PremiumCtaMarker[] = []

  for (const match of content.matchAll(BRACKET_CTA_REGEX)) {
    pushMarker(
      markers,
      match.index ?? 0,
      match[0],
      decodeMarkerEntities(match[1] || '').trim()
    )
  }

  for (const match of content.matchAll(CUSTOM_CTA_REGEX)) {
    pushMarker(
      markers,
      match.index ?? 0,
      match[0],
      getCustomTagPayload(match[1])
    )
  }

  for (const match of content.matchAll(ORPHAN_CTA_REGEX)) {
    pushMarker(
      markers,
      match.index ?? 0,
      match[0],
      decodePayloadText(
        match[1] ||
          match[2] ||
          match[3] ||
          match[4] ||
          match[5] ||
          match[6]
      )
    )
  }

  for (const match of content.matchAll(STANDALONE_CTA_REGEX)) {
    pushMarker(markers, match.index ?? 0, match[0], '')
  }

  return removeOverlappingMarkers(markers)
}

export const splitPremiumCtaSegments = (content: string | null | undefined) => {
  const source = content || ''
  const markers = findPremiumCtaMarkers(source)

  if (markers.length === 0) {
    return source
      ? [
          {
            text: source,
            type: 'text' as const
          }
        ]
      : []
  }

  const segments: PremiumCtaSegment[] = []
  let cursor = 0

  for (const marker of markers) {
    if (marker.start > cursor) {
      segments.push({
        text: source.slice(cursor, marker.start),
        type: 'text'
      })
    }

    segments.push({
      payloadText: marker.payloadText,
      type: 'cta'
    })
    cursor = marker.end
  }

  if (cursor < source.length) {
    segments.push({
      text: source.slice(cursor),
      type: 'text'
    })
  }

  return segments
}

export const createPremiumCtaCustomTag = (payloadText: string) =>
  `<get-premium-button data-payload="${encodeURIComponent(payloadText)}"></get-premium-button>`

export const replacePremiumCtaMarkers = (content: string) =>
  content
    .replace(BRACKET_CTA_REGEX, (_match, payload: string) =>
      createPremiumCtaCustomTag(payload || '')
    )
    .replace(STANDALONE_CTA_REGEX, () => createPremiumCtaCustomTag(''))
